import React, { useEffect, useState } from 'react';
import {
    Box,
    Tab,
    TextField,
    Typography
} from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import ButtonBox from '../../components/ButtonBox';
import CachedIcon from '@mui/icons-material/Cached';
import NewTableBox from '../../components/NewTableBox';
import EventLogApi from '../../api/EventLogApi';
import { useError, useLoading } from '../../utils/functions';
import usePaginationStore from '../../stores/PaginationStore';

const EventLog: React.FC = () => {
    const { setError } = useError();
    const { startLoading, stopLoading } = useLoading();
    const [value, setValue] = useState('1');

    const [formValues, setFormValues] = useState({
        begin: '',
        end: '',
    });

    const [data, setData] = useState([]);
    const [failData, setFailData] = useState([]);

    const [totalCount, setTotalCount] = useState(0);
    const { setBeforeCursor, setAfterCursor, setReQuery, pageAction, closeAction, resetStore } = usePaginationStore();

    const columns = [
        { field: "timestamp", title: "Time Stamp", isSort: true },
        { field: "email", title: "Email", isSearch: true },
        { field: "appName", title: "App Name" },
        { field: "requestUrl", title: "RequestURL" },
        { field: "httpMethod", title: "HttpMethod", isFilter: true, filterOptions: [{ value: "PUT", label: "PUT" }, { value: "DELETE", label: "DELETE" }, { value: "PATCH", label: "PATCH" }, { value: "POST", label: "POST" }, { value: "GET", label: "GET" }], },
        { field: "requestBody", title: "RequestBody" },
        { field: "exception", title: "Exception" },
    ];

    const failColumns = [
        { field: "timestamp", title: "Time Stamp", isSort: true },
        { field: "email", title: "Email", isSearch: true },
    ];

    const switchTab = (event: React.SyntheticEvent, newValue: string) => {
        setValue(newValue);
        setData([]);
        setFailData([]);
        resetStore();
    };

    const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormValues((prevValues) => ({ ...prevValues, [field]: e.target.value }));
    };

    const handleQuery = async () => {
        startLoading();
        try {

            setData([]);
            setFailData([]);
            setTotalCount(0);

            const { limit, beforeCursor, afterCursor } = usePaginationStore.getState();

            const paginationParams = {
                limit: limit,
                beforeCursor: beforeCursor,
                afterCursor: afterCursor,
            };

            if (value === '1') {
                const eventLogResponse = await EventLogApi.getUserActions(formValues.begin, formValues.end, paginationParams);

                if (eventLogResponse.code) {
                    setError(eventLogResponse.message);
                } else {
                    const parsedData = eventLogResponse.data.map((log: any) => ({
                        ...log,
                        requestBody: JSON.stringify(log.requestBody, null, 2),
                    }));

                    setData(parsedData);

                    if (eventLogResponse.metadata && eventLogResponse.metadata.total) {
                        setTotalCount(eventLogResponse.metadata.total);
                    }

                    if (eventLogResponse.paging) {
                        setAfterCursor(eventLogResponse.paging.afterCursor);
                        setBeforeCursor(eventLogResponse.paging.beforeCursor);
                    }
                }

            } else {
                const eventLogResponse = await EventLogApi.getFailedLogins(formValues.begin, formValues.end, paginationParams);
                if (eventLogResponse.code) {
                    setError(eventLogResponse.message);
                } else {
                    setFailData(eventLogResponse.data);
                }

                if (eventLogResponse.metadata && eventLogResponse.metadata.total) {
                    setTotalCount(eventLogResponse.metadata.total);
                }

                if (eventLogResponse.paging) {
                    setAfterCursor(eventLogResponse.paging.afterCursor);
                    setBeforeCursor(eventLogResponse.paging.beforeCursor);
                }

            }
        } catch (error) {
            console.error("Error eventLogResponse data:", error);
        } finally {
            closeAction();
            stopLoading();
        }
    };

    useEffect(() => {
        if (pageAction) {
            handleQuery();
        }
    }, [pageAction]);

    useEffect(() => {
        resetStore();
    }, []);

    return (
        <>
            <Typography className="layout-title">
                Event Log
            </Typography>

            <Box mb={4} display="flex" alignItems="center" gap={4} sx={{ mt: 4 }}>
                <Typography>Time</Typography>
                <TextField
                    type="datetime-local"
                    size="small"
                    className='w250'
                    value={formValues.begin}
                    onChange={handleChange('begin')}
                />
                <TextField
                    type="datetime-local"
                    size="small"
                    className='w250'
                    value={formValues.end}
                    onChange={handleChange('end')}
                />

                <ButtonBox
                    text="Search"
                    onClick={() => { setReQuery(true); handleQuery(); }}
                />
            </Box>

            <Box sx={{ width: '100%', typography: 'body1' }}>
                <TabContext value={value}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <TabList onChange={switchTab} aria-label="lab API tabs example">
                            <Tab label="User Actions" value="1" />
                            <Tab label="Failed Logins" value="2" />
                        </TabList>
                    </Box>
                    <TabPanel value="1">
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                            <Box />

                            <ButtonBox
                                text='Refresh'
                                onClick={handleQuery}
                                icon={<CachedIcon />} />

                        </Box>
                        <NewTableBox columns={columns} data={data} totalCount={totalCount} />
                    </TabPanel>
                    <TabPanel value="2">
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                            <Box />

                            <ButtonBox
                                text='Refresh'
                                onClick={handleQuery}
                                icon={<CachedIcon />} />

                        </Box>
                        <NewTableBox columns={failColumns} data={failData} showDisplayHeader={false} totalCount={totalCount} />
                    </TabPanel>
                </TabContext>
            </Box>
        </>
    );
};

export default EventLog;
